import React, { Fragment, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import {
  Button, Col, Container, DropdownItem, DropdownMenu, DropdownToggle,
  Form, FormGroup, Input, Label, Media, Row, UncontrolledDropdown,
} from 'reactstrap';
import { MoreVertical, PlusCircle } from 'react-feather';
import { FaPen, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { H4, Spinner } from '../../../../../AbstractElements';
import CommonModal from '../../../../UiKits/Modals/common/modal';
import { baseURL } from '../../../../../Services/api/baseURL';
import Loader from '../../../../Loader/Loader';

const MESSAGE_MAX_LENGTH = 300;

const initialForm = {
  message: '',
  order: 0,
  isActive: true,
};

const AnnouncementManagement = () => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const getToken = () => JSON.parse(localStorage.getItem('token'));

  const resetForm = () => setForm(initialForm);

  const toggleAddModal = () => {
    setAddModal((p) => !p);
    setEditData(null);
    resetForm();
  };

  const handleField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      const res = await axios.get(
        `${baseURL}/api/content-settings/announcements/items?${params.toString()}`,
        { headers: { Authorization: `${getToken()}` } }
      );
      setData(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleSubmit = async () => {
    if (!form.message.trim()) {
      Swal.fire({ title: 'Message is required', icon: 'warning', confirmButtonColor: '#d3178a' });
      return;
    }
    if (form.message.length > MESSAGE_MAX_LENGTH) {
      Swal.fire({ title: `Message must be ${MESSAGE_MAX_LENGTH} characters or fewer`, icon: 'warning', confirmButtonColor: '#d3178a' });
      return;
    }
    setSaving(true);
    try {
      if (editData) {
        await axios.patch(
          `${baseURL}/api/content-settings/announcements/items/${editData._id}`,
          form,
          { headers: { Authorization: `${getToken()}` } }
        );
        Swal.fire({ title: 'Announcement Updated!', icon: 'success', confirmButtonColor: '#d3178a' });
      } else {
        await axios.post(
          `${baseURL}/api/content-settings/announcements/items`,
          form,
          { headers: { Authorization: `${getToken()}` } }
        );
        Swal.fire({ title: 'Announcement Added!', icon: 'success', confirmButtonColor: '#d3178a' });
      }
      toggleAddModal();
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
      Swal.fire({ title: 'Failed to save announcement', icon: 'error', confirmButtonColor: '#d3178a' });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    setEditData(row);
    setForm({
      message: row.message || '',
      order: row.order || 0,
      isActive: row.isActive ?? true,
    });
    setAddModal(true);
  };

  const toggleVisibility = async (row) => {
    try {
      await axios.patch(
        `${baseURL}/api/content-settings/announcements/items/${row._id}`,
        { isActive: !row.isActive },
        { headers: { Authorization: `${getToken()}` } }
      );
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(
        `${baseURL}/api/content-settings/announcements/items/${deleteTarget._id}`,
        { headers: { Authorization: `${getToken()}` } }
      );
      setDeleteModal(false);
      setDeleteTarget(null);
      fetchAnnouncements();
      Swal.fire({ title: 'Announcement Deleted', icon: 'success', confirmButtonColor: '#d3178a' });
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = data.filter((item) =>
    `${item.message}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      name: '#',
      selector: (row) => row.order,
      sortable: true,
      center: true,
      width: '60px',
    },
    {
      name: 'Message',
      selector: (row) => row.message,
      grow: 3,
      cell: (row) => (
        <span style={{ fontWeight: 500 }}>{row.message}</span>
      ),
    },
    {
      name: 'Visibility',
      selector: (row) => row.isActive,
      center: true,
      width: '150px',
      cell: (row) => (
        <label className="d-flex align-items-center gap-2 cursor-pointer mb-0">
          <input
            type="checkbox"
            checked={!!row.isActive}
            onChange={() => toggleVisibility(row)}
            className="w-4 h-4 cursor-pointer"
            style={{ accentColor: '#d3178a' }}
          />
          <span className={row.isActive ? 'text-success' : 'text-danger'}>
            {row.isActive ? 'Visible' : 'Hidden'}
          </span>
        </label>
      ),
    },
    {
      name: 'Updated',
      selector: (row) => row.updatedAt,
      sortable: true,
      center: true,
      width: '110px',
      cell: (row) => moment(row.updatedAt).format('DD/MM/YYYY'),
    },
    {
      name: 'Actions',
      center: true,
      width: '80px',
      cell: (row) => (
        <UncontrolledDropdown className="action_dropdown">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem onClick={() => openEdit(row)}>
              Edit <FaPen size={12} />
            </DropdownItem>
            <DropdownItem
              style={{ color: '#e53e3e' }}
              onClick={() => { setDeleteTarget(row); setDeleteModal(true); }}
            >
              Delete <FaTrash size={12} />
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  return (
    <Fragment>
      <Container fluid style={{ paddingTop: '30px' }}>
        <Row>
          <Col sm="12">
            <div
              style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              }}
            >
              {/* Header */}
              <Row className="pb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <H4>Announcements</H4>
                    <p style={{ color: '#999', fontSize: '13px', margin: '4px 0 0' }}>
                      These are shown as a scrolling marquee on the customer site. Only visible announcements are shown, in order.
                    </p>
                  </div>
                  <div className="file-content">
                    <Media>
                      <div className="mb-0 form-group position-relative search_outer d-flex align-items-center me-2 justify-center">
                        <i className="fa fa-search"     style={{
                            left: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 1,
                          }}></i>
                        <input
                          className="form-control border-0"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          type="text"
                          placeholder="Search announcements..."
                        />
                      </div>
                      <Media body className="text-end">
                        <Button
                          className="btn btn-primary d-flex align-items-center gap-1"
                          onClick={toggleAddModal}
                          style={{ background: '#d3178a', border: 'none', borderRadius: '8px' }}
                        >
                          <PlusCircle size={16} />
                          Add Announcement
                        </Button>
                      </Media>
                    </Media>
                  </div>
                </div>
              </Row>

              {/* Table */}
              {loading ? (
                <Col xxl="12" className="flex justify-center items-center">
                  <div className="loader-box"><Spinner /></div>
                </Col>
              ) : (
                <DataTable
                  data={filtered}
                  columns={columns}
                  pagination
                  progressPending={loading}
                  progressComponent={<Loader />}
                  noDataComponent={
                    <div style={{ padding: '40px', color: '#999', textAlign: 'center' }}>
                      No announcements found. Click "Add Announcement" to get started.
                    </div>
                  }
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {/* Add / Edit Modal */}
      <CommonModal
        isOpen={addModal}
        title={editData ? 'Edit Announcement' : 'Add New Announcement'}
        className="store_modal"
        toggler={toggleAddModal}
        size="lg"
      >
        <Container>
          <Form>
            <FormGroup>
              <Label className="font-medium text-base">
                Message <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                rows="3"
                maxLength={MESSAGE_MAX_LENGTH}
                value={form.message}
                onChange={(e) => handleField('message', e.target.value)}
                placeholder="e.g. Free delivery on all orders above $50 this weekend!"
                style={{ borderRadius: '8px' }}
              />
              <p
                style={{
                  textAlign: 'right',
                  fontSize: '12px',
                  color: form.message.length >= MESSAGE_MAX_LENGTH ? '#e53e3e' : '#999',
                  margin: '4px 0 0',
                }}
              >
                {form.message.length}/{MESSAGE_MAX_LENGTH}
              </p>
            </FormGroup>

            <FormGroup>
              <Label className="font-medium text-base">Order</Label>
              <Input
                type="number"
                min="0"
                value={form.order}
                onChange={(e) => handleField('order', parseInt(e.target.value) || 0)}
                style={{ borderRadius: '8px', maxWidth: '150px' }}
              />
              <p style={{ fontSize: '12px', color: '#999', margin: '4px 0 0' }}>
                Lower numbers appear first in the marquee.
              </p>
            </FormGroup>

            <FormGroup>
              <label className="d-flex align-items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => handleField('isActive', e.target.checked)}
                  className="w-4 h-4 cursor-pointer"
                  style={{ accentColor: '#d3178a' }}
                />
                <span>Visible (shown in the marquee on the customer site)</span>
              </label>
            </FormGroup>

            <Row>
              <Col className="text-center">
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  style={{
                    background: '#d3178a',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '10px 40px',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                >
                  {saving ? 'Saving…' : editData ? 'Update Announcement' : 'Save Announcement'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Container>
      </CommonModal>

      {/* Delete Confirm */}
      <CommonModal
        isOpen={deleteModal}
        title="Delete Announcement"
        toggler={() => { setDeleteModal(false); setDeleteTarget(null); }}
        size="sm"
      >
        <Container className="d-flex flex-column align-items-center py-2">
          <p style={{ textAlign: 'center', color: '#555' }}>
            Are you sure you want to delete this announcement?
          </p>
          <p
            style={{
              fontWeight: 600,
              color: '#333',
              textAlign: 'center',
              fontSize: '14px',
            }}
          >
            "{deleteTarget?.message}"
          </p>
          <div className="d-flex gap-3 mt-2">
            <div
              onClick={() => { setDeleteModal(false); setDeleteTarget(null); }}
              style={{
                border: '1px solid #ccc',
                padding: '8px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                color: '#555',
              }}
            >
              Cancel
            </div>
            <div
              onClick={confirmDelete}
              style={{
                background: '#e53e3e',
                padding: '8px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                color: '#fff',
                fontWeight: 600,
              }}
            >
              Delete
            </div>
          </div>
        </Container>
      </CommonModal>
    </Fragment>
  );
};

export default AnnouncementManagement;
